import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BETSLIP_RESOLUTION_WS_EVENT } from '../constants';
import useTicketAuthorizationRoomId from './useTicketAuthorizationRoomId';
import useWebsocket, { WebsocketData } from './useWebsocket';

export type PendingTicketsWindow = {
  fromTimestamp: number;
  toTimestamp: number;
};

export const getPendingTicketsWindow = (): PendingTicketsWindow => ({
  fromTimestamp: dayjs().subtract(1, 'day').valueOf(),
  toTimestamp: dayjs().add(1, 'hour').valueOf(),
});

/**
 * Created-at window for the Ticket Authorization list. It is recomputed on every ticket
 * authorization event for the active marketplace and after a socket reconnect, so a page left
 * open for hours still lists new tickets (SPOR-4598) and tickets created while disconnected
 * are caught up. Changing the window changes the query key, which refetches the list.
 */
const usePendingTicketsWindow = (): PendingTicketsWindow => {
  const roomId = useTicketAuthorizationRoomId();
  const [pendingTicketsWindow, setPendingTicketsWindow] = useState(getPendingTicketsWindow);

  const refresh = useCallback(() => setPendingTicketsWindow(getPendingTicketsWindow()), []);

  const onMessage = useCallback(
    (data: WebsocketData<unknown>) => {
      const isTicketAuthorizationEvent = Object.values(data).some(
        (message) => message.id === roomId && message.event === BETSLIP_RESOLUTION_WS_EVENT
      );
      if (isTicketAuthorizationEvent) refresh();
    },
    [roomId, refresh]
  );

  const { isConnected } = useWebsocket({ skipCache: true, callback: onMessage });

  const wasConnected = useRef(isConnected);
  useEffect(() => {
    // Only a (re)connect after the page mounted: the initial fetch already covers mount time.
    if (isConnected && !wasConnected.current) refresh();
    wasConnected.current = isConnected;
  }, [isConnected, refresh]);

  return pendingTicketsWindow;
};

export default usePendingTicketsWindow;
